import { http, HttpResponse } from 'msw';
import { ActionRequest, ActionResponse, Claim, ClaimHistory } from 'interfaces';
import { ActionRequestStatus, ActionRequestType } from '@billing/validation';
import { claimTariffStatusEnumValues } from '@billing/validation/tariff';
import { buildClaim, buildDebit } from '../../factories';
import {
  canonicalClaims,
  claimStatusValues,
  healthcardRefs,
  patientRefs,
  payorRefs,
  providerRefs,
  statusRefs,
  tariffRefs,
  siteRefs,
} from '../../factories/seeds/entities';
import { mockHealthcards } from './healthcardMock';
import { mockSites } from './siteMock';
import { mockStatuses } from './statusMock';
import { mockPatients } from './patientMock';
import { mockProviders } from './providerMock';
import { mockPayors } from './payorMock';
import { mockTariffs } from './tariffMock';
import { mockSubmissions } from './submissionMock';
import { mockUsers } from './userMock';

const siteByID = new Map(mockSites.map((site) => [site.id, site]));
const patientByID = new Map(
  mockPatients.map((patient) => [patient.id, patient])
);
const providerByID = new Map(
  mockProviders.map((provider) => [provider.id, provider])
);
const payorByID = new Map(mockPayors.map((payor) => [payor.id, payor]));
const healthcardByID = new Map(
  mockHealthcards.map((healthcard) => [healthcard.id, healthcard])
);
const tariffByID = new Map(mockTariffs.map((tariff) => [tariff.id, tariff]));
const submissionByID = new Map(
  mockSubmissions.map((submission) => [submission.id, submission])
);

const toMockClaim = (claim: (typeof canonicalClaims)[number]): Claim =>
  buildClaim<Claim>({
    ...claim,
    patientID: claim.patientID ?? '',
    providerID: claim.providerID ?? '',

    siteID: claim.siteID ?? '',
    healthcardID: claim.healthcardID ?? '',
    payorID: claim.payorID ?? '',
    healthcard: healthcardByID.get(claim.healthcardID ?? '') ?? null,
    patient: patientByID.get(claim.patientID ?? '') ?? null,
    provider: providerByID.get(claim.providerID ?? '') ?? null,
    site: siteByID.get(claim.siteID ?? '') ?? null,
    status: claim.status ?? '',
    payor: payorByID.get(claim.payorID ?? '') ?? null,
    tariffs: (claim.tariffs ?? [])
      .map((tariff: any) => tariffByID.get(tariff.id))
      .filter((tariff): tariff is (typeof mockTariffs)[number] =>
        Boolean(tariff)
      ),
    submissions: (claim.submissions ?? [])
      .map((submission: any) => submissionByID.get(submission.id))
      .filter((submission): submission is (typeof mockSubmissions)[number] =>
        Boolean(submission)
      ),
    debits: (claim.debits ?? []).map((debit: any) =>
      buildDebit({
        ...debit,
        claimID: claim.id,
        payorID: debit.payorID ?? claim.payorID ?? '',
        providerID: debit.providerID ?? claim.providerID ?? '',
        payor: payorByID.get(debit.payorID ?? claim.payorID ?? '') ?? undefined,
        provider:
          providerByID.get(debit.providerID ?? claim.providerID ?? '') ??
          undefined,
        tariff: tariffByID.get(debit.tariffID ?? '') ?? undefined,
        eobs: debit.eobs ?? [],
      })
    ),
    claimHistories: claim.claimHistories ?? [],
    actionRequests: claim.actionRequests ?? [],
    actionResponses: claim.actionResponses ?? [],
  }) as Claim;

const canonicalMockClaims = canonicalClaims.map(toMockClaim);
const readyStatusID = statusRefs.ready.id;
const readyStatus = claimStatusValues.ready;

const incompleteStatus = claimStatusValues.incomplete;

const submittedStatus = claimStatusValues.submitted;

const paidClaimFive = canonicalMockClaims.find(
  (claim) => claim.id === 'd015a101-7a06-4071-9fec-17ee687acfbf'
);

const readyClaimFiveVariant = paidClaimFive
  ? buildClaim<Claim>({
      ...paidClaimFive,

      status: readyStatus,
    })
  : null;

export const mockClaims: Claim[] = readyClaimFiveVariant
  ? [...canonicalMockClaims, readyClaimFiveVariant]
  : canonicalMockClaims;

export const mockActionRequests: ActionRequest[] = [
  {
    id: 'action-req-1',
    status: ActionRequestStatus.SUBMITTED,
    type: ActionRequestType.INFORMATION,
    createdAt: new Date('2025-02-19T00:00:00.000Z'),
    message: 'Need more details',
    requestedByUser: mockUsers[0],
    requestedBy: mockUsers[0].id,
    providerID: mockClaims[0].providerID,
    claimID: mockClaims[0].id,
    parentRequestID: null,
  },
];

export const mockActionResponses: ActionResponse[] = [
  {
    id: 'action-res-1',
    actionRequestID: 'action-req-1',
    createdAt: new Date('2025-02-20T00:00:00.000Z'),
    message: 'Here you go',
    providerID: mockClaims[0].providerID,
    claimID: mockClaims[0].id,
    provider: providerByID.get(mockClaims[0].providerID) ?? mockProviders[0],
  },
];

const charlesHartPatient =
  patientByID.get(patientRefs.charlesHart.id) ?? mockPatients[3];
const brettsNurseProvider =
  providerByID.get(providerRefs.brettsNurse.id) ?? mockProviders[1];
const canadaLifeSite =
  siteByID.get(siteRefs.canadaLifeCentre.id) ?? mockSites[0];
const mshPayor = payorByID.get(payorRefs.msh.id) ?? mockPayors[1];
const testClaimHealthcard =
  healthcardByID.get(healthcardRefs.charlesHartPrimary.id) ??
  mockHealthcards[3];
const testClaimTariff =
  tariffByID.get(tariffRefs.testing8442.id) ?? mockTariffs[0];

const testClaimID = '09630ccb-2d56-42f5-825c-d3f85027b402';

export const testClaim: Claim = buildClaim<Claim>({
  ...(canonicalMockClaims.find(
    (claim) => claim.id === '326619d4-9fbc-43d9-9f1a-3534cc7ef2d1'
  ) ?? canonicalMockClaims[0]),
  id: testClaimID,
  version: 2,
  patientID: charlesHartPatient.id,
  providerID: brettsNurseProvider.id,

  siteID: canadaLifeSite.id,
  createdAt: '2024-02-12T15:01:32.000Z',
  claimNumber: '000008798',
  providerComments: 'Statement build history after strategy story.',
  clerkComments: 'Discuss edge election myself quite third throw.',
  chiroService: null,
  icd9: '75264',
  interpretingRadiologist: null,
  locationOfService: 'C',
  optometristReason: '25',
  patientCount: 1,
  referringProvider: '00000',
  serviceLocationIndicator: 'W',
  thirdPartyLiability: null,
  incorporated: '0',
  serviceStart: '2024-02-12T15:01:32.000Z',
  serviceEnd: '2024-02-12T15:01:32.000Z',
  healthcardID: testClaimHealthcard.id,
  updatedAt: '2024-02-12T15:01:32.000Z',
  payorID: mshPayor.id,
  healthcard: testClaimHealthcard,
  patient: charlesHartPatient,
  provider: brettsNurseProvider,
  site: canadaLifeSite,
  status: readyStatus,
  payor: mshPayor,
  tariffs: [testClaimTariff],
  overhead: 15,
  debits: [],
  claimHistories: [],
  messageID: '',
  hospitalName: '',
  hospitalCode: '',
  facilityNumber: '',
  groupID: null,
  group: null,
}) as Claim;

export const invalidClaim: Claim = buildClaim<Claim>({
  ...testClaim,

  status: incompleteStatus,
  icd9: null,
  referringProvider: null,
}) as Claim;

export const submittedClaim: Claim = buildClaim<Claim>({
  ...testClaim,

  status: submittedStatus,
}) as Claim;

export const mockClaimHistory: ClaimHistory[] = [
  {
    ...mockClaims[0],
    id: 'history-5563a744-9a0a-41d6-89b9-c6f5002cc3de',
    claimID: mockClaims[0].id,
    claim: mockClaims[0] as Claim,
    version: mockClaims[0].version + 1,
    debits: mockClaims[0].debits ?? [],
    submissions: mockClaims[0].submissions ?? [],
    changes: [
      {
        type: 'array',
        added: [
          {
            id: 'c7ae8a14-cf49-49dd-8557-45f0eee5c2d9',
            code: '8321',
            prefix: '7',
            icd9: '12345',
            rate: '54.60',
            type: 'Base',
            units: 1,
            description: 'VIRTUAL VISIT BY TELEPHONE OR VIDEO',
            effectiveDate: '2024-04-01',
            specialtyCode: '051',
          },
        ],
        after: [
          {
            id: 'c7ae8a14-cf49-49dd-8557-45f0eee5c2d9',
            code: '8321',
            prefix: '7',
            icd9: '12345',
            rate: '54.60',
            type: 'Base',
            units: 1,
            description: 'VIRTUAL VISIT BY TELEPHONE OR VIDEO',
            effectiveDate: '2024-04-01',
            specialtyCode: '051',
          },
        ],
        field: 'tariffs',
        before: [],
        removed: [],
        modified: [],
        changedBy: 'auto',
        updatedAt: '2024-12-13T16:57:54.075Z',
      },
      {
        type: 'relation',
        after: {
          id: mockClaims[0].provider?.id,
          license: mockClaims[0].provider?.license,
          lastName: mockClaims[0].provider?.lastName,
          firstName: mockClaims[0].provider?.firstName,
        },
        field: 'provider',
        before: null,
        changedBy: 'auto',
        updatedAt: '2024-12-13T16:57:54.075Z',
        differences: [
          {
            lhs: null,
            rhs: JSON.stringify({
              id: mockClaims[0].provider?.id,
              license: mockClaims[0].provider?.license,
              lastName: mockClaims[0].provider?.lastName,
              firstName: mockClaims[0].provider?.firstName,
            }),
            kind: 'E',
          },
        ],
      },
      {
        type: 'primitive',
        after: mockStatuses[0].id,
        field: 'statusID',
        before: readyStatusID,
        changedBy: 'auto',
        updatedAt: '2024-12-13T16:57:54.075Z',
      },
    ],
  },
];

export const mockClaimHandlers = [
  http.get('*/resource/claim', ({ request }) => {
    const id = new URL(request.url).searchParams.get('id');
    const claim = mockClaims.find((item) => item.id === id);
    if (!claim) {
      return HttpResponse.json({ message: 'Not Found' }, { status: 404 });
    }
    return HttpResponse.json(claim);
  }),
  http.get('*/resource/claim/site/page', ({ request }) => {
    const siteID = new URL(request.url).searchParams.get('siteID');
    return HttpResponse.json({
      data: mockClaims.filter((claim) => claim.siteID === siteID),
      nextPage: 1,
      total: mockClaims.length,
    });
  }),
  http.get('*/resource/claim/user/page', ({ request }) => {
    const userID = new URL(request.url).searchParams.get('userID');
    const claims = mockClaims.filter((claim) => {
      const provider = mockProviders.find(
        (item) => item.id === claim.providerID
      );
      return provider?.userID === userID;
    });
    return HttpResponse.json({
      data: claims,
      nextPage: 1,
      total: claims.length,
    });
  }),
  http.get('*/resource/claim/status/count', ({ request }) => {
    const siteID = new URL(request.url).searchParams.get('siteID');
    const claims = mockClaims.filter((claim) => claim.siteID === siteID);
    const statusCount = mockStatuses.reduce((acc, status) => {
      const count = claims.filter(
        (claim) => claim.status === status.type
      ).length;
      return {
        ...acc,
        [status.type]: count,
      };
    }, {});
    return HttpResponse.json(statusCount);
  }),
  http.get('*/resource/claim/tariff/status/count', ({ request }) => {
    const siteID = new URL(request.url).searchParams.get('siteID');
    const claims = mockClaims.filter((claim) => claim.siteID === siteID);
    const tariffStatusValues = Array.isArray(claimTariffStatusEnumValues)
      ? claimTariffStatusEnumValues
      : [];
    const tariffStatusCount = tariffStatusValues.reduce(
      (acc, status) => ({ ...acc, [status]: 0 }),
      {} as Record<string, number>
    );

    claims.forEach((claim) => {
      const tariffs = Array.isArray(claim.tariffs) ? claim.tariffs : [];
      const statusesOnClaim = new Set(
        tariffs
          .map((tariff) => (tariff as any)?.status)
          .filter((status): status is string => typeof status === 'string')
      );
      statusesOnClaim.forEach((status) => {
        if (tariffStatusCount[status] !== undefined) {
          tariffStatusCount[status] += 1;
        }
      });
    });

    return HttpResponse.json(tariffStatusCount);
  }),
  http.get('*/resource/claim/user/status/count', ({ request }) => {
    const userID = new URL(request.url).searchParams.get('userID');
    const claims = mockClaims.filter((claim) => {
      const provider = mockProviders.find(
        (item) => item.id === claim.providerID
      );
      return provider?.userID === userID;
    });
    const statusCount = mockStatuses.reduce((acc, status) => {
      const count = claims.filter(
        (claim) => claim.status === status.type
      ).length;
      return { ...acc, [status.type]: count };
    }, {} as Record<string, number>);
    return HttpResponse.json(statusCount);
  }),
  http.get('*/resource/claim/user/tariff/status/count', ({ request }) => {
    const userID = new URL(request.url).searchParams.get('userID');
    const claims = mockClaims.filter((claim) => {
      const provider = mockProviders.find(
        (item) => item.id === claim.providerID
      );
      return provider?.userID === userID;
    });
    const tariffStatusValues = Array.isArray(claimTariffStatusEnumValues)
      ? claimTariffStatusEnumValues
      : [];
    const tariffStatusCount = tariffStatusValues.reduce(
      (acc, status) => ({ ...acc, [status]: 0 }),
      {} as Record<string, number>
    );

    claims.forEach((claim) => {
      const tariffs = Array.isArray(claim.tariffs) ? claim.tariffs : [];
      const statusesOnClaim = new Set(
        tariffs
          .map((tariff) => (tariff as any)?.status)
          .filter((status): status is string => typeof status === 'string')
      );
      statusesOnClaim.forEach((status) => {
        if (tariffStatusCount[status] !== undefined) {
          tariffStatusCount[status] += 1;
        }
      });
    });

    return HttpResponse.json(tariffStatusCount);
  }),
  http.put('*/resource/claim', async ({ request }) => {
    const response = await request.json();
    return HttpResponse.json(response);
  }),
  http.get('*/resource/actionManagement/request/claim', ({ request }) => {
    const claimID = new URL(request.url).searchParams.get('claimID');
    const results = claimID
      ? mockActionRequests.filter((request) => request.claimID === claimID)
      : [];
    return HttpResponse.json(results);
  }),
  http.get('*/resource/actionManagement/response/claim', ({ request }) => {
    const claimID = new URL(request.url).searchParams.get('claimID');
    const results = claimID
      ? mockActionResponses.filter((response) => response.claimID === claimID)
      : [];
    return HttpResponse.json(results);
  }),
  http.get('*/resource/actionManagement/request/user', ({ request }) => {
    const userID = new URL(request.url).searchParams.get('userID');
    const results = userID
      ? mockActionRequests.filter(
          (request) =>
            request.requestedBy === userID ||
            request.requestedByUser?.id === userID
        )
      : [];
    return HttpResponse.json(results);
  }),
  http.get('*/resource/actionManagement/response/user', ({ request }) => {
    const userID = new URL(request.url).searchParams.get('userID');
    if (!userID) {
      return HttpResponse.json([]);
    }
    const providerIDs = mockProviders
      .filter((provider) => provider.userID === userID)
      .map((provider) => provider.id);
    const results = mockActionResponses.filter((response) =>
      providerIDs.includes(response.providerID as string)
    );
    return HttpResponse.json(results);
  }),
  http.post('*/resource/actionManagement/request', async ({ request }) => {
    const response = await request.json();
    return HttpResponse.json(response);
  }),
  http.post('*/resource/actionManagement/response', async ({ request }) => {
    const response = await request.json();
    return HttpResponse.json(response);
  }),
  http.put('*/resource/actionManagement/request', async ({ request }) => {
    const response = await request.json();
    return HttpResponse.json(response);
  }),
  http.put('*/resource/actionManagement/response', async ({ request }) => {
    const response = await request.json();
    return HttpResponse.json(response);
  }),
  http.get('*/resource/patient/*', ({ request }) => {
    const url = new URL(request.url);
    const id = url.pathname.split('/').pop();
    const patient = mockPatients.find((item) => item.id === id);
    if (!patient) {
      return HttpResponse.json({ message: 'Not Found' }, { status: 404 });
    }
    return HttpResponse.json(patient);
  }),
  http.get('*/resource/tariff/*', ({ request }) => {
    const url = new URL(request.url);
    const id = url.pathname.split('/').pop();
    const tariff = mockTariffs.find((item) => item.id === id);
    if (!tariff) {
      return HttpResponse.json({ message: 'Not Found' }, { status: 404 });
    }
    return HttpResponse.json(tariff);
  }),
  http.get('*/resource/claim/stale', ({ request }) => {
    const siteID = new URL(request.url).searchParams.get('siteID');
    const stale = mockClaims.filter(
      (claim) =>
        claim.siteID === siteID && claim.status === mockStatuses[0].type
    );
    return HttpResponse.json(stale);
  }),
  http.get('*/resource/claim/stale/count', ({ request }) => {
    const siteID = new URL(request.url).searchParams.get('siteID');
    const count = mockClaims.filter(
      (claim) =>
        claim.siteID === siteID && claim.status === mockStatuses[0].type
    ).length;
    return HttpResponse.json(count);
  }),
  http.get('*/resource/claim/tariff/resubmission/pending', () => {
    return HttpResponse.json([]);
  }),
  http.options('*', () => HttpResponse.json({}, { status: 200 })),
];

export const mockClaimStatusesCount: {
  [key: string]: number;
} = mockStatuses.reduce((acc, status) => {
  const count = mockClaims.filter(
    (claim) => claim.status === status.type
  ).length;
  return {
    ...acc,
    [status.type]: count,
  };
}, {});
